/**
 * Bootstrap 5 Button Utilities - Vanilla JavaScript replacements for jQuery button methods
 * These utilities provide Bootstrap 5 compatible button state management without jQuery dependency
 */

/**
 * Set button to loading state with spinner
 * @param {HTMLElement|string} button - Button element or ID
 * @param {string} loadingText - Optional loading text (default: 'Loading...')
 */
function setButtonLoading(button, loadingText = 'Loading...') {
    const btn = typeof button === 'string' ? document.getElementById(button) : button;
    if (!btn) return;

    // Store original state
    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.setAttribute('data-original-text', originalText);
    btn.setAttribute('data-original-disabled', btn.disabled);

    // Set loading state with Bootstrap 5 spinner
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${loadingText}`;
    btn.classList.add('btn-loading');
}

/**
 * Reset button from loading state
 * @param {HTMLElement|string} button - Button element or ID
 */
function resetButtonLoading(button) {
    const btn = typeof button === 'string' ? document.getElementById(button) : button;
    if (!btn) return;

    // Restore original state
    const originalText = btn.getAttribute('data-original-text');
    const wasDisabled = btn.getAttribute('data-original-disabled') === 'true';

    if (originalText) {
        btn.innerHTML = originalText;
        btn.removeAttribute('data-original-text');
        btn.removeAttribute('data-original-disabled');
    }

    btn.disabled = wasDisabled;
    btn.classList.remove('btn-loading');
}

/**
 * Toggle button disabled state
 * @param {HTMLElement|string} button - Button element or ID
 * @param {boolean} disabled - Whether to disable the button
 */
function toggleButton(button, disabled) {
    const btn = typeof button === 'string' ? document.getElementById(button) : button;
    if (!btn) return;

    btn.disabled = disabled;
    if (disabled) {
        btn.classList.add('disabled');
        btn.setAttribute('aria-disabled', 'true');
    } else {
        btn.classList.remove('disabled');
        btn.removeAttribute('aria-disabled');
    }
}

/**
 * Show/hide element (replaces jQuery show/hide)
 * @param {HTMLElement|string} element - Element or ID
 * @param {boolean} show - Whether to show (true) or hide (false)
 */
function toggleElementVisibility(element, show) {
    const el = typeof element === 'string' ? document.getElementById(element) : element;
    if (!el) return;

    if (show) {
        el.style.display = '';
        el.classList.remove('d-none');
    } else {
        el.style.display = 'none';
        el.classList.add('d-none');
    }
}

/**
 * Add click event listener with Bootstrap 5 compatibility
 * @param {string} selector - CSS selector
 * @param {Function} handler - Click event handler
 */
function addClickListener(selector, handler) {
    document.querySelectorAll(selector).forEach(function(element) {
        element.addEventListener('click', handler);
    });
}

/**
 * Trigger custom event on element (replaces jQuery trigger)
 * @param {HTMLElement|string} element - Element or ID
 * @param {string} eventName - Event name to trigger
 * @param {Object} data - Optional event data
 */
function triggerEvent(element, eventName, data) {
    const el = typeof element === 'string' ? document.getElementById(element) : element;
    if (!el) return;

    const event = new CustomEvent(eventName, { 
        detail: data,
        bubbles: true,
        cancelable: true 
    });
    el.dispatchEvent(event);
}

/**
 * Get/set element value (replaces jQuery val())
 * @param {HTMLElement|string} element - Element or ID
 * @param {string} value - Value to set (optional)
 * @returns {string} - Current value if getting
 */
function elementValue(element, value) {
    const el = typeof element === 'string' ? document.getElementById(element) : element;
    if (!el) return '';

    if (value !== undefined) {
        el.value = value;
        return value;
    }
    return el.value;
}

/**
 * Initialize Bootstrap 5 components that replaced jQuery equivalents
 */
function initBootstrap5Components() {
    // Initialize tooltips
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new bootstrap.Tooltip(tooltipTriggerEl);
    });

    // Initialize popovers
    const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
    popoverTriggerList.map(function (popoverTriggerEl) {
        return new bootstrap.Popover(popoverTriggerEl);
    });

    // Initialize modals
    const modalList = [].slice.call(document.querySelectorAll('.modal'));
    modalList.map(function (modalEl) {
        return new bootstrap.Modal(modalEl);
    });
}

/**
 * Document ready replacement for Bootstrap 5
 * @param {Function} callback - Function to execute when DOM is ready
 */
function onDocumentReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        callback();
    }
}

/* Initialize popover on specified selector
 * @param {string|Element|Array<string|Element>} selector -
 *   - A CSS selector string (e.g., '.my-class', '#myId') to select one or more elements.
 *   - A single DOM element.
 *   - An array of selector strings and/or DOM elements.
 * @param {Object} [options={}] - Options to pass to the Bootstrap Popover constructor (e.g., {trigger: 'hover', placement: 'left'}).
 * @param {boolean} [singleSelect=false] - If true, only the first matching element is initialized (used internally for recursion).
 */
function initializePopover(selector, options = {}, singleSelect = false) {
    if (Array.isArray(selector)) {
        selector.forEach(function(sel) {
            initializePopover(sel, options, true);
        });
        return;
    }
    const createPopover = function(popoverEl) {
        if (bootstrap.Popover.getInstance(popoverEl)) {
            bootstrap.Popover.getInstance(popoverEl).dispose();
        }
        if (options && Object.keys(options).length > 0) {
            new bootstrap.Popover(popoverEl, options);
        } else {
            new bootstrap.Popover(popoverEl);
        }
    };

    if (singleSelect) {
        const singleEl = document.querySelector(selector);

        if (singleEl) {
            createPopover(singleEl);
        }
    } else {
      const popovers = typeof selector === 'string' ?
       document.querySelectorAll(selector) : 
        selector instanceof Element ? [selector] : selector;
      
       popovers.forEach(function(element) {
            createPopover(element);
        });
    }
}

// Auto-initialize Bootstrap 5 components when DOM is ready
onDocumentReady(function() {
    initBootstrap5Components();
});

// Export utilities for global use
if (typeof window !== 'undefined') {
    window.Bootstrap5Utils = {
        setButtonLoading,
        resetButtonLoading,
        toggleButton,
        toggleElementVisibility,
        addClickListener,
        triggerEvent,
        elementValue,
        initBootstrap5Components,
        onDocumentReady,
        initializePopover
    };
}

//Bootstrap Show Tab Helper
function showTab(selector) {
    var tabEl = document.querySelector(selector);

    if (tabEl) {
        var tabTrigger = new bootstrap.Tab(tabEl);
        tabTrigger.show();
    }
}
